import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type {
  SaveSd849ConfigInput,
  SetSenderPatternsInput,
  SetStructuralRulesInput,
} from '@erp/rpc';

/** Suppliers that have a committed and/or authored 849 parsing config. */
export function useSd849ConfigSuppliers() {
  return useQuery(
    orpc.shipAndDebit.sd849Configs.listSuppliers.queryOptions({ input: {} }),
  );
}

/** The authored + committed config for one supplier (skips until one is picked). */
export function useSd849Config(supplierName: string | undefined) {
  return useQuery(
    orpc.shipAndDebit.sd849Configs.getConfig.queryOptions({
      input: supplierName ? { supplierName } : skipToken,
    }),
  );
}

/**
 * Which OTHER supplier configs already claim these sender addresses. Read-only;
 * skipped until at least one address is present so an empty editor stays quiet.
 */
export function useSd849SenderClaims(
  supplierName: string | undefined,
  addresses: string[],
) {
  return useQuery(
    orpc.shipAndDebit.sd849Configs.checkSenderClaims.queryOptions({
      input:
        supplierName && addresses.length > 0
          ? { supplierName, addresses }
          : skipToken,
    }),
  );
}

/**
 * Replace a supplier's sender addresses. Field-scoped on purpose: the server
 * merges into the row it locks, so this can't revert a whole-config save the
 * JSON editor made in between (see Sd849SupplierConfigService.setSenderPatterns).
 */
export function useSetSd849SenderPatterns() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (input: SetSenderPatternsInput) =>
      rpcClient.shipAndDebit.sd849Configs.setSenderPatterns(input),
    onSuccess: (result) => {
      if (result.saved) {
        showToast({
          title: 'Saved',
          message: `Sender addresses updated for ${result.saved.supplierName}`,
          color: 'success',
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.shipAndDebit.sd849Configs.getConfig.key(),
        });
      } else {
        showToast({
          title: result.conflict ? 'Conflict' : 'Not saved',
          message:
            result.validation.errors.join(' ') || 'Config failed validation',
          color: 'error',
        });
        // Refetch on conflict so "reload stored" shows the winner's addresses.
        if (result.conflict) {
          void queryClient.invalidateQueries({
            queryKey: orpc.shipAndDebit.sd849Configs.getConfig.key(),
          });
        }
      }
    },
    onError: (error: Error) =>
      showToast({ title: 'Error', message: error.message, color: 'error' }),
  });
}

/**
 * Replace a supplier's 849 structural rules. Field-scoped like the sender
 * addresses: the server merges into the row it locks (SD-86). The caller owns
 * the toast, so its message can be translated.
 */
export function useSetSd849StructuralRules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetStructuralRulesInput) =>
      rpcClient.shipAndDebit.sd849Configs.setStructuralRules(input),
    onSuccess: (result) => {
      if (result.saved || result.conflict) {
        void queryClient.invalidateQueries({
          queryKey: orpc.shipAndDebit.sd849Configs.getConfig.key(),
        });
      }
    },
  });
}

/** Validate a draft config against the schema (no writes). */
export function useValidateSd849Config() {
  return useMutation({
    mutationFn: (config: Record<string, unknown>) =>
      rpcClient.shipAndDebit.sd849Configs.validate({ config }),
  });
}

/** Dry-run a draft config against a stored sample email (no writes). */
export function useTestSd849Config() {
  return useMutation({
    mutationFn: (input: {
      sampleId: string;
      config: Record<string, unknown>;
    }) => rpcClient.shipAndDebit.sd849Configs.testAgainstSample(input),
  });
}

/** Validate + save an authored config; invalidates the supplier list + config. */
export function useSaveSd849Config() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (input: SaveSd849ConfigInput) =>
      rpcClient.shipAndDebit.sd849Configs.save(input),
    onSuccess: (result) => {
      if (result.saved) {
        showToast({
          title: 'Saved',
          message: `Config for ${result.saved.supplierName} saved as ${result.saved.status}`,
          color: 'success',
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.shipAndDebit.sd849Configs.listSuppliers.key(),
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.shipAndDebit.sd849Configs.getConfig.key(),
        });
      } else {
        showToast({
          title: result.conflict ? 'Conflict' : 'Not saved',
          message:
            result.validation.errors.join(' ') || 'Config failed validation',
          color: 'error',
        });
        // A conflict means the row moved: refetch so the editor's "reload stored"
        // offers the WINNER's config. Without this it would restore the same
        // stale buffer and hit the identical conflict on the next save.
        if (result.conflict) {
          void queryClient.invalidateQueries({
            queryKey: orpc.shipAndDebit.sd849Configs.getConfig.key(),
          });
        }
      }
    },
    onError: (error: Error) =>
      showToast({ title: 'Error', message: error.message, color: 'error' }),
  });
}
